// src/stores/stepper.js
import { defineStore } from 'pinia';
import axios from 'axios';
export const useStepperStore = defineStore('stepper', {
  persist: true,
  state: () => ({
    companyProfile: {
      name: '',
      location: {
        address: '',
        city: '',
        postalCode: '',
        country: '',
      },
      location_geo: null, // Hier werden die Geodaten (im GeoJSON-Format) gespeichert
      lastGeocodedAddress: '', // Merkt sich den zuletzt geocodeten Adress-String
      businessCategory: '',
      naceCodes: [],
      description: '',
    },
    wasteInfo: {
      foodGroups: [],
      foodSubgroups: [],
      surplusName: '',
      amount: {
        value: null,
        unit: 'kg', // Always set to 'kg' by default
      },
      frequency: {
        times: 1,
        interval: 'once',
      },
      seasonal: false,
      monthlyCapacities: [
        { name: 'January', capacity: 100, active: false },
        { name: 'February', capacity: 100, active: false },
        { name: 'March', capacity: 100, active: false },
        { name: 'April', capacity: 100, active: false },
        { name: 'May', capacity: 100, active: false },
        { name: 'June', capacity: 100, active: false },
        { name: 'July', capacity: 100, active: false },
        { name: 'August', capacity: 100, active: false },
        { name: 'September', capacity: 100, active: false },
        { name: 'October', capacity: 100, active: false },
        { name: 'November', capacity: 100, active: false },
        { name: 'December', capacity: 100, active: false },
      ],
      additionalSurplusInfo: '',
    },
    solutions: [],
    selectedSolution: null,
    
    // Neu: Details der Lösungen (Scores und Reasoning) speichern
    solutionDetails: {
      scores: {},
      reasoning: {}
    }
  }),
  actions: {
    setCompanyProfile(profile) {
      this.companyProfile = profile;
    },
    
    setWasteInfo(newWasteInfo) {
      // Create a deep clone of the incoming object to avoid modifying the original
      const processedWasteInfo = JSON.parse(JSON.stringify(newWasteInfo));
      
      // If amount is being updated, ensure unit is always 'kg'
      if (processedWasteInfo.amount) {
        processedWasteInfo.amount = {
          ...processedWasteInfo.amount,
          unit: 'kg'
        };
      }
      
      // Log the change for debugging
      console.log('Setting wasteInfo with enforced kg unit:', {
        originalUnit: newWasteInfo.amount?.unit,
        processedUnit: processedWasteInfo.amount?.unit
      });
      
      // Merge with existing wasteInfo
      this.wasteInfo = { ...this.wasteInfo, ...processedWasteInfo };
    },
    
    // Method specifically for setting the amount value while enforcing 'kg' as unit
    setAmount(value) {
      this.wasteInfo.amount = {
        value: value,
        unit: 'kg'
      };
    },
    
    setSolutions(solutions) {
      this.solutions = solutions;
    },
    
    setSelectedSolution(solution) {
      this.selectedSolution = solution;
    },
    
    // BUG-FIX: Neue Methode zum vollständigen Zurücksetzen der Lösungsdaten
    resetSolutionData() {
      this.solutions = [];
      this.selectedSolution = null;
      this.solutionDetails = {
        scores: {},
        reasoning: {}
      };
      console.log("Reset all solution data");
    },
    
    // BUG-FIX: Neue Methode zum expliziten Löschen der Lösungsdetails
    clearSolutionDetails() {
      this.solutionDetails = {
        scores: {},
        reasoning: {}
      };
      console.log("Cleared solution details");
    },
    
    // Neu: Setzt die Detailinformationen für Lösungen
    setSolutionDetails(details) {
      console.log("Setting solution details:", details);
      if (!details) {
        this.solutionDetails = { scores: {}, reasoning: {} };
        return;
      }
      
      // Stellen Sie sicher, dass wir ein korrektes Format haben
      const formattedDetails = {
        scores: details.scores || {},
        reasoning: details.reasoning || {}
      };
      
      this.solutionDetails = formattedDetails;
      console.log("Updated solution details in store:", this.solutionDetails);
    },
    async geocodeCompanyAddress() {
      const { address, city, postalCode, country } = this.companyProfile.location;
      // Baue einen String, der die Adresse repräsentiert
      const newAddress = `${address || ''}, ${postalCode || ''} ${city || ''}, ${country || ''}`.trim();
      // Falls wir bereits Geodaten haben und der Adress-String gleich ist, brauchen wir nichts zu tun.
      if (this.companyProfile.location_geo && this.companyProfile.lastGeocodedAddress === newAddress) {
        return;
      }
      try {
        const response = await axios.post('/geocode', {
          address,
          city,
          postalCode,
          country,
        });
        if (response.data && response.data.found && response.data.location_geo) {
          this.companyProfile.location_geo = response.data.location_geo;
          this.companyProfile.lastGeocodedAddress = newAddress;
        } else {
          this.companyProfile.location_geo = null;
          this.companyProfile.lastGeocodedAddress = newAddress;
        }
      } catch (error) {
        console.error("Geocoding error:", error);
        this.companyProfile.location_geo = null;
        this.companyProfile.lastGeocodedAddress = newAddress;
      }
    },
    
    // Helfer-Methode um den Score für eine bestimmte Lösung zu erhalten
    getSolutionScore(solutionId) {
      if (!solutionId) return null;
      
      // Debugging-Ausgabe für IDs und Scores
      console.log("Looking for score for solutionId:", solutionId);
      console.log("Available scores:", this.solutionDetails.scores);
      
      // VCG-Lösungen haben möglicherweise andere ID-Formate
      // Direkt im scores-Objekt nachsehen
      if (this.solutionDetails?.scores?.[solutionId] !== undefined) {
        return this.solutionDetails.scores[solutionId];
      }
      
      // ID könnte in verschiedenen Formaten vorliegen, versuchen wir andere Varianten
      // 1. Bei string-basierten IDs, versuchen wir die ID ohne Präfix
      if (typeof solutionId === 'string') {
        // Versuchen Sie alle möglichen Formate der Solution-IDs
        const possibleIds = [
          solutionId,
          solutionId.toString(),
          // Bei MongoDB ObjectIDs könnten wir numerische IDs haben
          parseInt(solutionId)
        ];
        
        for (const id of possibleIds) {
          if (id && this.solutionDetails?.scores?.[id] !== undefined) {
            console.log(`Found score using ID format: ${id}`);
            return this.solutionDetails.scores[id];
          }
        }
      }
      
      console.log(`No score found for solution ID: ${solutionId}`);
      return null;
    },
    
    // Helfer-Methode um das Reasoning für eine bestimmte Lösung zu erhalten
    getSolutionReasoning(solutionId) {
      if (!solutionId) return null;
      
      // Debugging-Ausgabe für IDs und Reasoning
      console.log("Looking for reasoning for solutionId:", solutionId);
      console.log("Available reasoning:", this.solutionDetails.reasoning);
      
      // Direkt im reasoning-Objekt nachsehen
      if (this.solutionDetails?.reasoning?.[solutionId] !== undefined) {
        return this.solutionDetails.reasoning[solutionId];
      }
      
      // ID könnte in verschiedenen Formaten vorliegen, versuchen wir andere Varianten
      if (typeof solutionId === 'string') {
        // Versuchen Sie alle möglichen Formate der Solution-IDs
        const possibleIds = [
          solutionId,
          solutionId.toString(),
          // Bei MongoDB ObjectIDs könnten wir numerische IDs haben
          parseInt(solutionId)
        ];
        
        for (const id of possibleIds) {
          if (id && this.solutionDetails?.reasoning?.[id] !== undefined) {
            console.log(`Found reasoning using ID format: ${id}`);
            return this.solutionDetails.reasoning[id];
          }
        }
      }
      
      console.log(`No reasoning found for solution ID: ${solutionId}`);
      return null;
    },
    
    // Reset entire store to initial state
    resetStore() {
      this.companyProfile = {
        name: '',
        location: {
          address: '',
          city: '',
          postalCode: '',
          country: '',
        },
        location_geo: null,
        lastGeocodedAddress: '',
        businessCategory: '',
        naceCodes: [],
        description: '',
      };
      
      this.wasteInfo = {
        foodGroups: [],
        foodSubgroups: [],
        surplusName: '',
        amount: {
          value: null,
          unit: 'kg',
        },
        frequency: {
          times: 1,
          interval: 'once',
        },
        seasonal: false,
        monthlyCapacities: [
          { name: 'January', capacity: 100, active: false },
          { name: 'February', capacity: 100, active: false },
          { name: 'March', capacity: 100, active: false },
          { name: 'April', capacity: 100, active: false },
          { name: 'May', capacity: 100, active: false },
          { name: 'June', capacity: 100, active: false },
          { name: 'July', capacity: 100, active: false },
          { name: 'August', capacity: 100, active: false },
          { name: 'September', capacity: 100, active: false },
          { name: 'October', capacity: 100, active: false },
          { name: 'November', capacity: 100, active: false },
          { name: 'December', capacity: 100, active: false },
        ],
        additionalSurplusInfo: '',
      };
      
      this.solutions = [];
      this.selectedSolution = null;
      this.solutionDetails = {
        scores: {},
        reasoning: {}
      };
      
      console.log("Stepper store has been reset to initial state");
    }
  },
  getters: {
    // Add a getter to validate that amount unit is 'kg'
    isValidUnit() {
      return this.wasteInfo.amount.unit === 'kg';
    },
    
    // Neu: Prüft, ob Scores verfügbar sind
    hasScores() {
      return this.solutionDetails && 
             this.solutionDetails.scores && 
             Object.keys(this.solutionDetails.scores).length > 0;
    },
    
    // Neu: Prüft, ob Reasoning verfügbar ist
    hasReasoning() {
      return this.solutionDetails && 
             this.solutionDetails.reasoning && 
             Object.keys(this.solutionDetails.reasoning).length > 0;
    }
  }
});